import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import OperatorForm from './OperatorForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { ActiveStatusPill } from '../../../shared/Pill';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

// Operators — what an Organisation does with the products it buys (e.g.
// Retail, Integration, Service). An Organisation can hold several, one of
// them primary; independent of its Markets and Channels.
function OperatorTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/operators', { params: { status } });
      setRows(res.data.data || []);
    } catch (err) {
      setRows([]);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Operator "${item.name}"? It can no longer be added to an Organisation. Organisations that already have it keep it.`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/operators/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/operators/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/operators/${editItem.id}`, data);
      } else {
        await axios.post('/api/operators', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Operators</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Operator
            </button>
          )}
        </div>
      </div>
      <div style={{ margin: '8px 24px 0', fontSize: 13, color: '#5a6b85' }}>
        What an Organisation does with the products it buys from you: Retail resells them, Integration builds or installs with them, Service uses them to serve clients.
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="styled-input" value={status} onChange={(e) => setStatus(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <OperatorForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '60%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>In Use</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer' }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.name}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                  {item.usage_count ? (
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
                      title="See which Organisations have this Operator"
                      onClick={() => navigate(`/market-intel-all-entities?operator_id=${item.id}&operator_name=${encodeURIComponent(item.name)}`)}
                    >
                      {item.usage_count}
                    </button>
                  ) : '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  <ActiveStatusPill active={item.is_active} />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      item.is_active
                        ? { key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }
                        : { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OperatorTable;
